import { useState, useMemo } from 'react'
import { UserCheck, UserX, Edit2, Phone, MessageSquareText, Users } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { setDocument, deleteDocument } from '../../services/firebase'
import { useAuth } from '../../context/AuthContext'
import { formatRelativeTime } from '../../utils/helpers'
import { Button, Modal, Badge, Checkbox } from '../../components/common'

// Approve a pending worker or edit an approved worker's departments
const DepartmentAssignModal = ({ worker, departments, mode, onConfirm, onClose }) => {
  const [selected, setSelected] = useState(worker.departmentIds || worker.departmentInterests || [])
  const [saving, setSaving] = useState(false)

  const toggle = (slug) => {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    )
  }

  const handleConfirm = async () => {
    setSaving(true)
    await onConfirm(worker, selected)
    setSaving(false)
    onClose()
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="font-medium text-dark-100">{worker.displayName}</p>
        <p className="text-sm text-gray-500">{worker.email}{worker.phone ? ` · ${worker.phone}` : ''}</p>
        {worker.departmentInterests?.length > 0 && (
          <p className="text-sm text-gray-500 mt-1">
            Interested in: {worker.departmentInterests.join(', ')}
          </p>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Departments</p>
        <div className="space-y-2">
          {departments.map((dept) => (
            <Checkbox
              key={dept.id}
              label={dept.name}
              checked={selected.includes(dept.id)}
              onChange={() => toggle(dept.id)}
            />
          ))}
          {departments.length === 0 && (
            <p className="text-sm text-gray-500">
              No departments yet — create them on the Departments page first.
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" loading={saving} onClick={handleConfirm}>
          {mode === 'approve' ? 'Approve Worker' : 'Save Departments'}
        </Button>
      </div>
    </div>
  )
}

const WorkersManager = () => {
  const { user } = useAuth()
  const [assignModal, setAssignModal] = useState(null) // { worker, mode }
  const [rejectConfirm, setRejectConfirm] = useState(null)

  const { data: workers, update } = useCollection('users', {
    orderByField: null,
    whereClause: { field: 'role', operator: '==', value: 'worker' },
  })
  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const pending = useMemo(
    () => (workers || []).filter((w) => w.status === 'pending'),
    [workers]
  )
  const others = useMemo(
    () => (workers || []).filter((w) => w.status !== 'pending'),
    [workers]
  )

  // Keep the denormalized department rosters in sync with a worker's
  // departmentIds — portal members never read other users' docs, they read
  // departments/{slug}/members instead.
  const syncDepartmentMembers = async (worker, departmentIds) => {
    await Promise.all(
      (departments || []).map((dept) => {
        const path = `departments/${dept.id}/members`
        if (departmentIds.includes(dept.id)) {
          return setDocument(path, worker.id, {
            uid: worker.id,
            displayName: worker.displayName || worker.email,
          })
        }
        return deleteDocument(path, worker.id).catch(() => {})
      })
    )
  }

  const handleApprove = async (worker, departmentIds) => {
    await update(worker.id, {
      status: 'approved',
      departmentIds,
      approvedAt: new Date().toISOString(),
      approvedBy: user?.uid || null,
    })
    await syncDepartmentMembers(worker, departmentIds)
  }

  const handleEditDepartments = async (worker, departmentIds) => {
    await update(worker.id, { departmentIds })
    await syncDepartmentMembers(worker, departmentIds)
  }

  const handleReject = async () => {
    if (!rejectConfirm) return
    await update(rejectConfirm.id, { status: 'rejected' })
    await syncDepartmentMembers(rejectConfirm, [])
    setRejectConfirm(null)
  }

  const toggleActive = async (worker) => {
    await update(worker.id, { isActive: worker.isActive === false })
  }

  const deptName = (slug) => departments?.find((d) => d.id === slug)?.name || slug

  const statusBadge = (worker) => {
    if (worker.isActive === false) return <Badge variant="gray" size="sm">Paused</Badge>
    if (worker.status === 'approved') return <Badge variant="success" size="sm">Approved</Badge>
    if (worker.status === 'rejected') return <Badge variant="danger" size="sm">Rejected</Badge>
    return <Badge variant="warning" size="sm">Pending</Badge>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-dark-100">Workers</h1>
        <p className="text-gray-500">Approve registrations and manage portal access</p>
      </div>

      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="font-heading font-semibold text-dark-100 mb-4">
            Awaiting Approval ({pending.length})
          </h2>
          <div className="space-y-3">
            {pending.map((worker) => (
              <div
                key={worker.id}
                className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg p-4"
              >
                <div>
                  <p className="font-medium text-dark-100">{worker.displayName}</p>
                  <p className="text-sm text-gray-500">
                    {worker.email}
                    {worker.phone ? ` · ${worker.phone}` : ''}
                    {worker.createdAt ? ` · registered ${formatRelativeTime(worker.createdAt)}` : ''}
                  </p>
                  {worker.departmentInterests?.length > 0 && (
                    <p className="text-sm text-gray-500">
                      Interested in: {worker.departmentInterests.map(deptName).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={UserCheck}
                    onClick={() => setAssignModal({ worker, mode: 'approve' })}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={UserX}
                    onClick={() => setRejectConfirm(worker)}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All workers */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Worker</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Contact</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Departments</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {others.map((worker) => (
              <tr key={worker.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <p className="font-medium text-dark-100">{worker.displayName}</p>
                  <p className="text-sm text-gray-500">{worker.email}</p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    {worker.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {worker.phone}
                      </span>
                    )}
                    {worker.smsConsent && (
                      <Badge variant="info" size="sm" icon={MessageSquareText}>
                        SMS
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {(worker.departmentIds || []).map((slug) => (
                      <Badge key={slug} variant="primary" size="sm">
                        {deptName(slug)}
                      </Badge>
                    ))}
                    {(worker.departmentIds || []).length === 0 && (
                      <span className="text-sm text-gray-400">None</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">{statusBadge(worker)}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {worker.status === 'approved' && (
                      <>
                        <button
                          onClick={() => setAssignModal({ worker, mode: 'edit' })}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Edit departments"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <Button
                          variant={worker.isActive === false ? 'primary' : 'ghost'}
                          size="sm"
                          onClick={() => toggleActive(worker)}
                        >
                          {worker.isActive === false ? 'Reactivate' : 'Pause'}
                        </Button>
                      </>
                    )}
                    {worker.status === 'rejected' && (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={UserCheck}
                        onClick={() => setAssignModal({ worker, mode: 'approve' })}
                      >
                        Approve
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {others.length === 0 && pending.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No worker registrations yet. Workers sign up at /workers/register</p>
          </div>
        )}
      </div>

      {/* Approve / edit departments */}
      <Modal
        isOpen={!!assignModal}
        onClose={() => setAssignModal(null)}
        title={assignModal?.mode === 'approve' ? 'Approve Worker' : 'Edit Departments'}
      >
        {assignModal && (
          <DepartmentAssignModal
            worker={assignModal.worker}
            departments={departments || []}
            mode={assignModal.mode}
            onConfirm={assignModal.mode === 'approve' ? handleApprove : handleEditDepartments}
            onClose={() => setAssignModal(null)}
          />
        )}
      </Modal>

      {/* Reject confirmation */}
      <Modal
        isOpen={!!rejectConfirm}
        onClose={() => setRejectConfirm(null)}
        title="Reject Registration"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Reject {rejectConfirm?.displayName || 'this registration'}? They will see an
          "account inactive" screen and can contact the church office.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setRejectConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReject}>
            Reject
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default WorkersManager
